import React, { useEffect, useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useParams } from "react-router-dom";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import "./GroupLoanStatement.css";
import { fetchUserProfile } from "../redux/slices/branch/userProfileSlice";
import axios from "axios";

/* ===================== Utils ===================== */
const formatDate = (dateStr) => {
  if (!dateStr) return "-";
  const date = new Date(dateStr);
  return isNaN(date) ? dateStr : date.toLocaleDateString("en-GB");
};

const formatAmount = (amount) =>
  !amount ? "Rs 0" : `Rs ${Number(amount).toLocaleString("en-IN")}`;

/* ===================== Component ===================== */
const GroupLoanStatement = () => {
  const { loanId } = useParams();
  const dispatch = useDispatch();
  const cardRef = useRef();

  const location = useLocation();
  const loan = location.state?.loan;

  const { profile } = useSelector((state) => state.userProfile);

  const [emis, setEmis] = useState([]);
  const [company, setCompany] = useState(null);
  const [photoUrl, setPhotoUrl] = useState(null);
  const [customerDetails, setCustomerDetails] = useState(null);

  const [branchSealUrl, setBranchSealUrl] = useState(null);
  const [branchSignUrl, setBranchSignUrl] = useState(null);

  const branchId = Number(localStorage.getItem("userId")) || 0;

  /* ===================== Fetch Profile ===================== */
  useEffect(() => {
    dispatch(fetchUserProfile());
  }, [dispatch]);

  /* ===================== Fetch Company ===================== */
  useEffect(() => {
    if (!profile?.superAdminId) return;

    const fetchCompany = async () => {
      try {
        const res = await fetch(
          `https://payyug.in/api/get-company?superAdminId=${profile.superAdminId}`
        );
        const data = await res.json();
        if (res.ok && data) {
          if (data.logo) {
            data.logoUrl = `https://payyug.in/${data.logo.replace(/^images\//, "")}`;
          }
          if (data.qrCodeImage) {
            data.qrCodeUrl = `https://payyug.in/${data.qrCodeImage.replace(/^images\//, "")}`;
          }
          setCompany(data);
        }
      } catch (err) {
        console.error("Error fetching company:", err);
      }
    };

    fetchCompany();
  }, [profile?.superAdminId]);

  /* ===================== Fetch EMI List ===================== */
  useEffect(() => {
    if (!loanId || !profile?.superAdminId || !loan) return;

    const fetchEmis = async () => {
      try {
        const res = await axios.get(
          `https://payyug.in/api/group-loan/emi/list-filter?loanId=${loanId}`,
          {
            params: {
              superAdminId: profile.superAdminId,
              branchId: loan.branchId,
              grouploanAccountId: loan.id,
              limit: 100,
            },
          }
        );
        setEmis(res.data?.data || []);
      } catch (err) {
        console.error("Fetch EMI Error:", err);
        setEmis([]);
      }
    };

    fetchEmis();
  }, [loanId, profile?.superAdminId, loan]);

  /* ===================== Fetch Customer Photo ===================== */
  useEffect(() => {
    if (!loan?.member?.userId || !profile?.superAdminId) return;

    const fetchCustomerDetails = async () => {
      try {
        const res = await fetch(
          `https://payyug.in/api/customers-get-userId?userId=${loan.member.userId}&branchId=${branchId}`
        );
        const data = await res.json();
        setCustomerDetails(data.data);
      } catch (err) {
        console.error("Customer fetch error:", err);
      }
    };

    const fetchCustomerPhoto = async () => {
      try {
        const res = await fetch(
          `https://payyug.in/api/list-kyc?userId=${loan.member.userId}&superAdminId=${profile.superAdminId}&createdById=${branchId}`
        );
        const data = await res.json();
        if (res.ok && data.documents?.length > 0) {
          const photoDoc = data.documents.find(
            (doc) => doc.docName?.toLowerCase() === "photo"
          );
          if (photoDoc?.docImage) {
            setPhotoUrl(`https://payyug.in/uploads/${photoDoc.docImage}`);
          }
        }
      } catch (err) {
        console.error("Failed to fetch customer photo:", err);
      }
    };

    fetchCustomerDetails();
    fetchCustomerPhoto();
  }, [loan?.member?.userId, profile?.superAdminId, branchId]);

  /* ===================== Fetch Stamp and Sign ===================== */
  useEffect(() => {
    const fetchSealSign = async () => {
      if (!profile?.superAdminId) return;

      try {
        const res = await axios.get(
          `https://payyug.in/api/list/stamp?superAdminId=${profile?.superAdminId}&category=Group Loan Card`
        );

        const dataArray = res?.data?.data?.[0] || [];

        if (dataArray && dataArray.file) {
          const cleanSealPath = dataArray.file.replace(/^images\//, "");
          setBranchSealUrl(`https://payyug.in/${cleanSealPath}`);
        } else {
          setBranchSealUrl(null);
        }

        if (dataArray && dataArray.type) {
          const cleanSignPath = dataArray.type.replace(/^images\//, "");
          setBranchSignUrl(`https://payyug.in/${cleanSignPath}`);
        } else {
          setBranchSignUrl(null);
        }
      } catch (error) {
        console.error("API Fetch Error:", error);
      }
    };

    fetchSealSign();
  }, [profile?.superAdminId]);

  /* ===================== Derived Values ===================== */
  const emiAmountFromSchedule =
    emis && emis.length > 0 ? emis[0].emiAmount : 0;

  /* ===================== Optimized PDF Generator ===================== */
  const generatePDF = async () => {
    const input = cardRef.current;
    if (!input) return;

    const actionButtons = document.querySelectorAll(
      ".group-loan-statement-download-btn"
    );
    actionButtons.forEach((btn) => (btn.style.display = "none"));

    try {
      const pdf = new jsPDF({
        orientation: "p",
        unit: "mm",
        format: "a4",
        compress: true,
      });

      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const margin = 10;
      const contentWidth = pageWidth - margin * 2;
      const contentHeight = pageHeight - margin * 2;

      const canvas = await html2canvas(input, {
        scale: 1.5,
        useCORS: true,
        backgroundColor: "#ffffff",
        scrollY: -window.scrollY,
      });

      const imgWidth = canvas.width;
      const pixelPageHeight = (imgWidth * contentHeight) / contentWidth;

      const targetElements = input.querySelectorAll(".print-row");
      const inputRect = input.getBoundingClientRect();
      let breakPoints = [];

      targetElements.forEach((el) => {
        const rect = el.getBoundingClientRect();
        const relativeTop = (rect.top - inputRect.top) * 1.5;
        breakPoints.push(relativeTop);
      });

      let heightLeft = canvas.height;
      let sY = 0;

      while (heightLeft > 0) {
        let sHeight = pixelPageHeight;

        if (heightLeft > pixelPageHeight) {
          const expectedPageEnd = sY + pixelPageHeight;
          let bestBreakPoint = expectedPageEnd;

          for (let i = 0; i < breakPoints.length; i++) {
            if (breakPoints[i] > sY + 40 && breakPoints[i] < expectedPageEnd) {
              bestBreakPoint = breakPoints[i];
            }
          }

          sHeight = bestBreakPoint - sY;
        } else {
          sHeight = heightLeft;
        }

        const pageCanvas = document.createElement("canvas");
        pageCanvas.width = imgWidth;
        pageCanvas.height = sHeight;

        const ctx = pageCanvas.getContext("2d");
        ctx.fillStyle = "#ffffff";
        ctx.fillRect(0, 0, pageCanvas.width, pageCanvas.height);
        ctx.drawImage(canvas, 0, sY, imgWidth, sHeight, 0, 0, imgWidth, sHeight);

        const pageImgData = pageCanvas.toDataURL("image/jpeg", 0.75);
        const displayHeight_mm = (sHeight * contentWidth) / imgWidth;

        if (sY > 0) pdf.addPage();

        pdf.addImage(
          pageImgData,
          "JPEG",
          margin,
          margin,
          contentWidth,
          displayHeight_mm,
          undefined,
          "FAST"
        );

        sY += sHeight;
        heightLeft -= sHeight;
      }

      pdf.save(`${loan?.memberName || "Customer"}_GroupLoanStatement.pdf`);
    } catch (error) {
      console.error("PDF generation error:", error);
    } finally {
      actionButtons.forEach((btn) => (btn.style.display = "inline-block"));
    }
  };

  /* ===================== Print Logic ===================== */
  const triggerPrintElement = (elementRef, title = "Print_Document") => {
    const input = elementRef.current;
    if (!input) return;

    const existingIframe = document.getElementById("print-iframe");
    if (existingIframe) {
      document.body.removeChild(existingIframe);
    }

    const iframe = document.createElement("iframe");
    iframe.id = "print-iframe";
    iframe.style.position = "absolute";
    iframe.style.left = "-9999px";
    iframe.style.top = "0";
    iframe.style.width = "1024px";
    iframe.style.height = "auto";
    iframe.style.border = "none";
    document.body.appendChild(iframe);

    const doc = iframe.contentWindow.document;

    let extractedStyles = "";
    document.querySelectorAll("style, link[rel='stylesheet']").forEach((node) => {
      extractedStyles += node.outerHTML;
    });

    doc.open();
    doc.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>${title}</title>
          ${extractedStyles}
          <style>
            @page {
              size: A4 portrait;
              margin: 8mm 10mm;
            }
            *, *::before, *::after {
              -webkit-print-color-adjust: exact !important;
              print-color-adjust: exact !important;
              box-sizing: border-box !important;
              visibility: visible !important;
              opacity: 1 !important;
            }
            body {
              background: #ffffff !important;
              color: #000000 !important;
              font-family: Arial, Helvetica, sans-serif !important;
              margin: 0 !important;
              padding: 0 !important;
            }
            .group-loan-statement-container {
              width: 100% !important;
              max-width: 100% !important;
              margin: 0 auto !important;
              padding: 0 !important;
              border: none !important;
              box-shadow: none !important;
              display: block !important;
            }
            .group-loan-statement-header {
              display: flex !important;
              flex-direction: row !important;
              justify-content: space-between !important;
              align-items: center !important;
              padding: 10px 14px !important;
              border-bottom: 1.5px solid #000000 !important;
            }
            .group-loan-statement-logo-box {
              width: 85px !important;
              height: 85px !important;
              display: flex !important;
              align-items: center !important;
              justify-content: center !important;
              overflow: hidden !important;
              flex-shrink: 0 !important;
            }
            .group-loan-statement-logo {
              max-width: 100% !important;
              max-height: 100% !important;
              width: auto !important;
              height: auto !important;
              object-fit: contain !important;
              border-radius: 50% !important;
              display: block !important;
            }
            .group-loan-statement-details-box {
              text-align: center !important;
              flex: 1 !important;
            }
            .group-loan-statement-details-box h2 {
              font-size: 16px !important;
              font-weight: bold !important;
              margin: 0 0 4px 0 !important;
              color: #000000 !important;
            }
            .group-loan-statement-details-box p {
              font-size: 10px !important;
              line-height: 1.35 !important;
              margin: 0 !important;
              color: #000000 !important;
            }
            .group-loan-statement-title-section {
              text-align: center !important;
              margin: 10px 0 !important;
            }
            .group-loan-statement-title {
              font-size: 14px !important;
              font-weight: bold !important;
              text-decoration: underline !important;
              margin: 0 !important;
            }
            table, .group-loan-statement-detail-table, .group-loan-statement-table {
              width: 100% !important;
              border-collapse: collapse !important;
              color: #000000 !important;
              margin-bottom: 10px !important;
            }
            th, td {
              border: 1px solid #000000 !important;
              padding: 5px 6px !important;
              font-size: 10px !important;
              color: #000000 !important;
            }
            th {
              background-color: #f1f5f9 !important;
              font-weight: bold !important;
            }
            tr {
              page-break-inside: avoid !important;
            }
            .branch-loan-card-footer {
              display: flex !important;
              flex-direction: row !important;
              justify-content: space-between !important;
              padding: 15px 10px 10px !important;
              margin-top: 15px !important;
              page-break-inside: avoid !important;
            }
            .box-size {
              flex: 1 !important;
              text-align: center !important;
              font-size: 10px !important;
              font-weight: 600 !important;
            }
            .border-line {
              border-top: 1px dashed #000000 !important;
              margin-top: 20px !important;
            }
            .footer-seal-sign {
              display: flex !important;
              flex-direction: column !important;
              align-items: center !important;
              justify-content: center !important;
              gap: 4px !important;
            }
            .footer-seal img {
              width: 55px !important;
              height: 55px !important;
              object-fit: contain !important;
              display: block !important;
            }
            .footer-sign img {
              width: 100px !important;
              height: 35px !important;
              object-fit: contain !important;
              display: block !important;
            }
            img {
              display: inline-block !important;
              visibility: visible !important;
              max-width: 100% !important;
              -webkit-print-color-adjust: exact !important;
              print-color-adjust: exact !important;
            }
            button, .group-loan-statement-download-btn, [class*="download-btn"], [class*="print-btn"] {
              display: none !important;
            }
          </style>
        </head>
        <body>
          <div class="group-loan-statement-container">
            ${input.innerHTML}
          </div>
        </body>
      </html>
    `);
    doc.close();

    const checkAndPrint = () => {
      const iframeImages = doc.querySelectorAll("img");
      const promises = Array.from(iframeImages).map((img) => {
        if (img.complete) return Promise.resolve();
        return new Promise((resolve) => {
          img.onload = resolve;
          img.onerror = resolve;
        });
      });

      Promise.all(promises).then(() => {
        setTimeout(() => {
          iframe.contentWindow.focus();
          iframe.contentWindow.print();
        }, 250);
      });
    };

    if (doc.readyState === "complete") {
      checkAndPrint();
    } else {
      iframe.contentWindow.onload = checkAndPrint;
    }
  };

  const handlePrint = () => {
    triggerPrintElement(
      cardRef,
      `${loan?.memberName || "Customer"}_GroupLoanStatement`
    );
  };

  /* ===================== Safety ===================== */
  if (!loan) {
    return (
      <div className="group-loan-statement-container">
        No loan found for Loan ID {loanId}
      </div>
    );
  }

  return (
    <div>
      <div className="group-loan-statement-container" ref={cardRef}>
        {/* Header */}
        <header className="group-loan-statement-header">
          <div className="group-loan-statement-logo-box">
            {company?.logoUrl && (
              <img
                src={company.logoUrl}
                alt="Logo"
                className="group-loan-statement-logo"
              />
            )}
          </div>

          <div className="group-loan-statement-details-box">
            <h2>{company?.name || "Company Name"}</h2>
            <p>
              <strong>CIN:</strong> {company?.cin || "-"}
              <br />
              {company?.address || "-"}
              <br />
              <strong>Website:</strong> {company?.domain || "-"}
              <br />
              <strong>Contact:</strong> {company?.contact || "-"}
              <br />
              <strong>Email:</strong> {company?.email || "-"}
            </p>
          </div>
          {/* <div style={{ width: "85px", flexShrink: 0 }}></div> */}
        </header>

        {/* Title */}
        <div className="group-loan-statement-title-section">
          <h3 className="group-loan-statement-title">Group Loan Statement</h3>
        </div>

        {/* Loan Details */}
        <table className="group-loan-statement-detail-table">
          <tbody>
            <tr>
              <td>
                <strong>Member Name:</strong> {loan.memberName || "-"}
              </td>
              <td>
                <strong>Loan Account No:</strong>{" "}
                {loan.loanAccountNumber || loan.loanNumber}
              </td>
            </tr>

            <tr>
              <td>
                <strong>Center Name:</strong> {loan.centerName || "-"}
              </td>
              <td>
                <strong>Group Name:</strong> {loan.groupName || "-"}
              </td>
            </tr>
            <tr>
              <td colSpan={2}>
                <strong>Address: </strong>
                {customerDetails?.address || "-"}
              </td>
            </tr>
            <tr>
              <td>
                <strong>Branch :</strong> {profile?.branchName || "-"}
              </td>
              <td>
                <strong>Disbursal Date :</strong>{" "}
                {loan?.disbursalDate
                  ? new Date(loan?.disbursalDate).toLocaleDateString("en-GB")
                  : "N/A"}
              </td>
            </tr>
          </tbody>
        </table>

        {/* EMI Table */}
        <section className="group-loan-statement-table-section">
          <table className="group-loan-statement-table">
            <thead>
              <tr>
                <th>Sr. No</th>
                <th>EMI Amount</th>
                {loan?.loanType !== "FIXED" && (
                  <>
                    <th>Interest</th>
                    <th>Principal</th>
                  </>
                )}
                <th>Emi Due Date</th>
                <th>Payment Date</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {emis.map((emi, idx) => (
                <tr key={idx} className="print-row">
                  <td>{idx + 1}</td>
                  <td>{formatAmount(emi.emiAmount)}</td>
                  {loan?.loanType !== "FIXED" && (
                    <>
                      <td>{formatAmount(emi.interestComponent)}</td>
                      <td>{formatAmount(emi.principalComponent)}</td>
                    </>
                  )}
                  <td>{formatDate(emi.dueDate)}</td>
                  <td>{formatDate(emi.paidDate || emi.paymentDate)}</td>
                  <td>{emi.status}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>

        {/* Footer */}
        <footer className="branch-loan-card-footer print-row">
          <div className="box-size">
            Member Signature
            <br />
            (सदस्य के हस्ताक्षर)
            <div className="border-line"></div>
          </div>
          <div className="box-size">
            Spouse Signature
            <br />
            (जीवनसाथी के हस्ताक्षर)
            <div className="border-line"></div>
          </div>
          <div className="footer-seal-sign box-size">
            Branch Seal With Signature
            <br />
            (शाखा मोहर के साथ हस्ताक्षर)
            <div className="border-line"></div>
            {branchSealUrl && (
              <div className="footer-seal">
                <img src={branchSealUrl} alt="Seal" />
              </div>
            )}
            {branchSignUrl && (
              <div className="footer-sign">
                <img src={branchSignUrl} alt="Signature" />
              </div>
            )}
          </div>
        </footer>
      </div>

      {/* Action Buttons */}
      <div
        style={{
          textAlign: "center",
          display: "flex",
          justifyContent: "center",
          gap: "12px",
          marginTop: "16px",
        }}
      >
        <button
          onClick={handlePrint}
          className="group-loan-statement-download-btn"
          style={{
            padding: "9px 18px",
            backgroundColor: "#0a74da",
            color: "#ffffff",
            border: "none",
            borderRadius: "6px",
            fontSize: "13px",
            fontWeight: "600",
            cursor: "pointer",
          }}
        >
          🖨️ Print Statement
        </button>

        <button
          onClick={generatePDF}
          className="group-loan-statement-download-btn"
          style={{
            padding: "9px 18px",
            backgroundColor: "#0a74da",
            color: "#ffffff",
            border: "none",
            borderRadius: "6px",
            fontSize: "13px",
            fontWeight: "600",
            cursor: "pointer",
          }}
        >
          📄 Download PDF
        </button>
      </div>
    </div>
  );
};

export default GroupLoanStatement;